import type { StyleRule } from '@archipro-design/aria';
import {
    buttonClassName,
    productInfoSlotClassNames,
    productTileSlotClassNames,
    captionClassName,
    pxArrayToRem,
    pxToRem,
} from '@archipro-design/aria';
import { PADDING_LEFT_RIGHT_SECTION_MOBILE } from '../../common/pagebuilder.style';
import { PROJECT_REVAMP_CAPTION_TEXT } from '../../common/image-caption/ImageCaption.style';
import { COLUMN_WIDTH_100 } from '@modules/page-builder/config/column-constants';
import type { ColumnDimensionType } from '@modules/page-builder/types';

const PRODUCT_INFO_MIN_HEIGHT = 110;

// Width of one image in a symmetrical portrait duo ("Double symmetrical
// photos"): the duo grid caps at COLUMN_WIDTH_100 with a 3rem (48px) column
// gap in the project revamp, and each portrait image fills its 1fr track
// (DuoImageModule.style). Revamp portrait photo-and-paragraph images match it.
const DUO_PORTRAIT_IMAGE_WIDTH = (COLUMN_WIDTH_100 - 48) / 2;

export const DetailSingleImageContainer: StyleRule<{
    isTagging?: boolean;
    dimension?: ColumnDimensionType;
    isLinked?: boolean;
    moduleType?: string;
    isPageBuilder?: boolean;
    inViewPort?: boolean;
    isProjectRevamp?: boolean;
}> = ({
    isTagging,
    theme,
    dimension,
    isLinked,
    moduleType,
    isPageBuilder,
    isProjectRevamp,
}) => ({
    position: 'relative',
    width: dimension?.mobileWidth ? pxToRem(dimension.mobileWidth) : '100%',
    height: dimension?.mobileHeight ? pxToRem(dimension.mobileHeight) : '100%',

    [theme.screen.laptop]: {
        width: dimension?.width ? pxToRem(dimension.width) : '100%',
        height: dimension?.height ? pxToRem(dimension.height) : '100%',

        ...(moduleType === 'SingleImageModule' && {
            width: '100% !important',
            aspectRatio: '3/2',
            height: 'auto',
            transition: 'all 0.5s ease-in-out',
        }),

        // Project detail revamp (BC-4725): the Figma gallery draws the large
        // single image module as a centred 114.5rem box (1832px on the 1920
        // frame — 44px viewport insets, a 19:10 ratio). Express it as the
        // gallery content width plus 0.25rem per side (the 3rem gutter minus
        // the 2.75rem inset) capped at 114.5rem: between the 744px fela
        // laptop breakpoint and the 1024px Tailwind `lg` root-font switch the
        // root is the mobile-scale 3.865vw, where a bare 114.5rem is ~4x the
        // viewport. The margin centres the overhang (auto margins resolve to
        // 0 on overflow) and is clamped to the same 0.25rem for that band.
        ...(moduleType === 'SingleImageModule' &&
            isProjectRevamp && {
                width: 'calc(100% + 0.5rem) !important',
                maxWidth: '114.5rem',
                marginLeft: 'max(-0.25rem, calc((100% - 114.5rem) / 2))',
                marginRight: '0',
                aspectRatio: '19 / 10',
                height: 'auto',
            }),

        // Full page images should have no side margins (take full viewport width)
        ...(moduleType === 'FullPageImageModule' && {
            width: '100%',
            height: dimension?.height ? pxToRem(dimension.height) : '100%',
            marginLeft: '0',
            marginRight: '0',
        }),

        // Project detail revamp (BC-4725): the full-page image is a true
        // edge-to-edge bleed — break out of the gallery section's horizontal
        // padding (ProjectGallerySection's `md:px-[3rem]`, mirrored here) so
        // the image spans the viewport with no side margin or padding.
        ...(moduleType === 'FullPageImageModule' &&
            isProjectRevamp && {
                width: 'calc(100% + 6rem)',
                marginLeft: '-3rem',
            }),

        ...(moduleType === 'FeaturedProductModule' && {
            maxWidth: '100% !important',
            width: dimension?.width ? pxToRem(dimension.width) : '100%',
            // Vite 8: fixed px height prevents layout="fill" images from painting.
            height: 'auto',
            ...(dimension?.width &&
                dimension?.height && {
                    aspectRatio: `${dimension.width} / ${dimension.height}`,
                }),
        }),

        ...(moduleType === 'MediumSingleImageModule' && {
            width: dimension?.width ? pxToRem(dimension.width * 1.26) : '100%', // Other elements are 1.26x bigger so we're scaling this along
            height: dimension?.height
                ? pxToRem(dimension.height * 1.26)
                : '100%',
        }),

        ...(moduleType === 'ImageParagraphModule' && {
            // Project detail revamp: a portrait photo-and-paragraph image
            // renders at the same width as a symmetrical duo image — the tall
            // 2:3 crop at the legacy 1050px is disproportionately large next
            // to the paragraph.
            width: pxToRem(
                isProjectRevamp && dimension?.isPortrait
                    ? DUO_PORTRAIT_IMAGE_WIDTH
                    : 1050,
            ),
            ...(dimension?.isPortrait
                ? { aspectRatio: '2/3', height: 'auto' }
                : { aspectRatio: '3/2', height: 'auto' }),
        }),

        // For FeaturedProductModule align to the right on desktop
        ...(moduleType === 'FeaturedProductModule' && {
            marginLeft: 'auto',
            marginRight: 0,
        }),

        ...(moduleType === 'DuoImageModule' &&
            !isPageBuilder && {
                width: '100%',
                height: 'auto',
                ...(dimension &&
                    dimension.width > 0 &&
                    dimension.height > 0 && {
                        aspectRatio: `${dimension.width} / ${dimension.height}`,
                    }),
            }),

        // Project detail revamp (BC-4725): the trio grid caps its total width
        // at 114.4rem (TrioImageModule.style), so each image fills its 1fr
        // track instead of the fixed CDN crop width, keeping the crop ratio.
        ...(moduleType === 'TrioImageModule' &&
            isProjectRevamp && {
                width: '100%',
                height: 'auto',
                ...(dimension &&
                    dimension.width > 0 &&
                    dimension.height > 0 && {
                        aspectRatio: `${dimension.width} / ${dimension.height}`,
                    }),
            }),
    },

    '&:hover': {
        '& .hover-buttons-image': {
            opacity: isTagging ? 0 : 1,
            visibility: isTagging ? 'hidden' : 'visible',
        },
    },
    cursor: !isTagging && isLinked ? 'pointer' : 'default',
    margin: moduleType === 'ImageParagraphModule' ? '0' : 'auto', // Left align for image paragraph module
});

export const SingleImageWithProductContainer: StyleRule<{
    dimension?: ColumnDimensionType;
    variant?: '01' | '02';
    moduleType?: string;
    isPageBuilder?: boolean;
}> = ({ theme, dimension, variant, moduleType, isPageBuilder }) => ({
    position: 'relative',
    width: dimension?.mobileWidth ? pxToRem(dimension.mobileWidth) : '100%',
    height: 'auto',
    margin: '0 auto',
    maxWidth: '100%',
    minHeight: dimension?.mobileHeight
        ? pxToRem(dimension.mobileHeight + PRODUCT_INFO_MIN_HEIGHT)
        : 0,

    [`& .${productInfoSlotClassNames.container}`]: {
        padding:
            variant === '02' ? pxArrayToRem([0, 18]) : pxArrayToRem([0, 12]),
    },

    [`& .${productTileSlotClassNames.variants} .${captionClassName}`]: {
        fontSize: pxToRem(12),
    },

    [theme.screen.laptop]: {
        maxWidth: '100%',
        width: (() => {
            // No dimension available or in page builder - use full width
            if (!dimension?.width || isPageBuilder) {
                return '100%';
            }

            // Featured product paragraph layout uses the image width, not the gallery 1.26x scale.
            if (moduleType === 'FeaturedProductModule') {
                return pxToRem(dimension.width);
            }

            // Standard modules use exact dimension width
            return pxToRem(dimension.width);
        })(),
        minHeight: dimension?.height
            ? pxToRem(
                  (moduleType === 'FeaturedProductModule'
                      ? dimension.height
                      : dimension.height * 1.26) + PRODUCT_INFO_MIN_HEIGHT,
              )
            : 0,
        [`& .${productInfoSlotClassNames.container}`]: {
            padding: 0,
        },
        ...(moduleType === 'FeaturedProductModule' && {
            // Vite 8: base `margin: 0 auto` centres the block; master right-aligns it.
            margin: 0,
            marginLeft: 'auto',
            marginRight: 0,
        }),
        // Ensure product CTA button has no left margin so it aligns with image edges
        [`& .${buttonClassName}`]: {
            ...(moduleType === 'FeaturedProductModule' && {
                marginLeft: 0,
            }),
        },

        [`& .${productTileSlotClassNames.variants} .${captionClassName}`]: {
            fontSize: pxToRem(16),
        },
    },
});

export const GalleryProductContainer: StyleRule<{
    dimension?: ColumnDimensionType;
    variant?: '01' | '02';
}> = ({ theme, dimension, variant }) => ({
    position: 'relative',
    width: '100%', // Take full width of gallery container
    height: 'auto',
    minHeight: dimension?.mobileHeight
        ? pxToRem(dimension.mobileHeight + PRODUCT_INFO_MIN_HEIGHT)
        : 0,

    [`& .${productInfoSlotClassNames.container}`]: {
        padding:
            variant === '02' ? pxArrayToRem([0, 18]) : pxArrayToRem([0, 12]),
    },

    [`& .${productTileSlotClassNames.variants} .${captionClassName}`]: {
        fontSize: pxToRem(12),
    },

    [theme.screen.laptop]: {
        width: '100%', // Always take full width
        minHeight: dimension?.height
            ? pxToRem(dimension.height + PRODUCT_INFO_MIN_HEIGHT)
            : 0,
        [`& .${productInfoSlotClassNames.container}`]: {
            padding: 0,
        },

        [`& .${productTileSlotClassNames.variants} .${captionClassName}`]: {
            fontSize: pxToRem(16),
        },
    },
});

export const ImageCaptionContainer: StyleRule<{
    dimension?: ColumnDimensionType;
    moduleType?: string;
    isProject?: boolean;
    isProjectRevamp?: boolean;
    isPageBuilder?: boolean;
}> = ({
    theme,
    dimension,
    moduleType,
    isProject,
    isProjectRevamp,
    isPageBuilder,
}) => ({
    flexGrow: 1,
    position: 'relative',
    width: '100%',
    maxWidth: dimension?.mobileWidth ? pxToRem(dimension.mobileWidth) : 'unset',
    height: 'auto',
    margin: '0',

    // Target the caption element specifically to ensure styles apply
    [`& .${captionClassName}`]: {
        fontSize: pxToRem(22),
        lineHeight: '120%',
        letterSpacing: '0.015em',
        fontWeight: 450,
        color: '#161616',
        fontFamily: 'neue-haas-grotesk-display',
    },

    [theme.screen.laptop]: {
        maxWidth: '100%',
        ...((moduleType === 'SingleImageModule' ||
            moduleType === 'FullPageGalleryModule') &&
            isProject && {
                width: isPageBuilder ? '100%' : pxToRem(1300),
                maxWidth: isPageBuilder
                    ? dimension?.width
                        ? pxToRem(dimension.width)
                        : '100%'
                    : 'unset',
            }),

        // Project detail revamp (BC-4642): the caption is a fixed 25rem column
        // sitting at the image's left edge rather than the legacy fixed
        // 1300px. The image is a centred 114.5rem-capped box that overhangs
        // the gallery gutter by up to 0.25rem per side, so shift the caption
        // by the image's exact margin (same clamped formula as
        // DetailSingleImageContainer) to keep it on the image's left edge.
        // The caption text style itself lives in the shared isProjectRevamp
        // block below.
        ...(moduleType === 'SingleImageModule' &&
            isProjectRevamp && {
                width: '25rem',
                maxWidth: '100%',
                marginLeft: 'max(-0.25rem, calc((100% - 114.5rem) / 2))',
                marginRight: 'auto',
            }),
        ...(moduleType === 'FullPageImageModule' && {
            width: '100%',
            maxWidth: pxToRem(1820),

            marginLeft: 'auto',
            marginRight: 'auto',

            [`& .${captionClassName}`]: {
                fontSize: `${pxToRem(20)} !important`,
            },
        }),
        // Full-page revamp (BC-4725): the image itself bleeds edge-to-edge
        // (see DetailSingleImageContainer), but its caption "matches the rest
        // of the page" — a 25rem column at the same content-left edge as the
        // large single image's caption, not the bled image edge. Same clamped
        // offset so it lines up with every other module's caption.
        ...(moduleType === 'FullPageImageModule' &&
            isProjectRevamp && {
                width: '25rem',
                maxWidth: '100%',
                marginLeft: 'max(-0.25rem, calc((100% - 114.5rem) / 2))',
                marginRight: 'auto',
            }),
        ...(moduleType === 'MediumSingleImageModule' && {
            width: dimension?.width ? pxToRem(dimension.width * 1.26) : '100%',
            maxWidth: dimension?.width
                ? pxToRem(dimension.width * 1.26)
                : '100%',
            marginLeft: 'auto',
            marginRight: 'auto',
        }),
        ...(moduleType === 'ImageParagraphModule' && {
            maxWidth: '100%',
            margin: '0 !important',
            // Match the image width for image paragraph module (revamp
            // portrait images use the symmetrical duo image width).
            width: pxToRem(
                isProjectRevamp && dimension?.isPortrait
                    ? DUO_PORTRAIT_IMAGE_WIDTH
                    : 1050,
            ),
        }),

        // Project detail revamp: every module's caption shares the Figma text
        // style in a 25rem block at the image's left edge. Spread last so it
        // replaces the per-module caption styles above (notably the full-page
        // image's 20px). Containers keep their per-module widths — single and
        // full-page revamp left-align to their image; centred modules (medium
        // single) stay aligned to theirs — and only the text block is capped.
        // Colour/family/weight are also forced white + font-sans by the
        // gallery section; these keep the block correct on its own too.
        ...(isProjectRevamp && {
            [`& .${captionClassName}`]: PROJECT_REVAMP_CAPTION_TEXT,
        }),
    },
});

export const MobileCaptionContainer: StyleRule = ({ theme }) => ({
    padding: pxArrayToRem([0, PADDING_LEFT_RIGHT_SECTION_MOBILE]),
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    flexDirection: 'column',
    [theme.screen.tablet]: {
        padding: 0,
    },
});

export const DetailSingleImageLinkedIcon: StyleRule = () => ({
    zIndex: 1,
    position: 'absolute',
    bottom: pxToRem(12),
    right: pxToRem(12),

    [`& .${buttonClassName}`]: {
        borderRadius: '50%',
        padding: pxToRem(8),
        width: pxToRem(48),
        height: pxToRem(48),
    },
});
